import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import theme from "assets/theme";
import { Helmet } from "react-helmet";
import { HashLink } from "react-router-hash-link";

// Components
import { Alert, Container, Grid } from "@mui/material";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import MKBox from "components/MKBox";
import CBLoading from "components/CBLoading";
import ContactInformation from "./components/contactInformation";
import BillingAddress from "./components/billingAddress";
import PaymentOptions from "./components/paymentOptions";
import CartSidebar from "pages/Shop/components/Cart/components/cartSidebar";
import CustomAlert from "./components/addressValidationAlert";
// Styled components
import { StyledMainPageContainer } from "index.styles";
import { StyledCheckoutContainer } from "./index.styles";
// Formik
import { Formik, Form } from "formik";
// Utils & Hooks
import { CartContext } from "utils/context/cart.context";
import { CheckoutContext } from "utils/context/checkout.context";
import { constructPurchaseMadeData } from "utils/helpers/constructPurchaseMadeData";
import { scrollToTop } from "utils/helpers/scrollToTop";
import useMediaQueries from "utils/mediaQueries.utils";
import useStripeTransaction from "pages/Checkout/hooks/useStripeTransaction";
import usePurchaseMade from "./hooks/usePurchaseMade";
import useCreateStripeAccount from "./hooks/useCreateStripeAccount";
import useCreateEhrAccount from "./hooks/useCreateEhrAccount";
import useGoogleAddressValidationSimplified from "./hooks/useGoogleAddressValidationSimplified";
import useFetchExecutionArnResults from "../../utils/hooks/useFetchExecutionArnResults";
import { initialValues, validationSchema, validationSchema2 } from "./formSchema";

const Checkout = () => {
  const navigate = useNavigate();
  const { isLg } = useMediaQueries();
  const { validateAddress, addressValidationLoading, addressValidationError } = useGoogleAddressValidationSimplified();
  const { stripeAccountLoading, stripeAccountError, createStripeAccount } = useCreateStripeAccount();
  const { ehrAccountLoading, ehrAccountError, createEHRAccount } = useCreateEhrAccount();
  const { processPayment, stripeTransactionLoading, stripeTransactionError } = useStripeTransaction();
  const { fetchExecutionArnResults } = useFetchExecutionArnResults();
  const { purchaseMade, purchaseAutomationLoading, purchaseAutomationError } = usePurchaseMade();
  const [checkoutErrorStatus, setCheckoutErrorStatus] = useState(null);
  const [setTimeoutIsLoading, setSetTimeoutIsLoading] = useState(false);
  const [setTimeoutError, setSetTimeoutError] = useState(null);
  // ! state values for address validation alert component
  const [addressErrors, setAddressErrors] = useState([]);
  const [addressSuggestions, setAddressSuggestions] = useState([]);

  const { cartItems, cartTotal, stripeCouponObj, discount, originalSubtotal, getCartProductType } = useContext(CartContext);

  const { showBillingAddressFormFields } = useContext(CheckoutContext);
  const hasPackageItem = cartItems.some((item) => item.metadata?.type === "package" || item.metadata?.type === "ehr-subscription");

  const navigateToOrderConfirmation = (paymentData) => {
    // console.log("navigating: " + JSON.stringify(paymentData, null, 2));
    navigate("/order-confirmation", { state: paymentData });
  };

  const handleSubmit = async (values) => {
    scrollToTop();
    setCheckoutErrorStatus(null);
    try {
      //* Google Address Validation
      const addressValidationResponse = await validateAddress(values);
      if (!addressValidationResponse) {
        setCheckoutErrorStatus(addressValidationError || "Error validating address.");
        return;
      }
      const { isValid, errors, suggestions } = addressValidationResponse;

      if (!isValid) {
        setAddressErrors(errors);
        setAddressSuggestions(suggestions);
        return;
      } else if (isValid) {
        setAddressErrors(null);
        setAddressSuggestions(null);
      }

      // Check if this is an EHR subscription checkout
      const isEhrSubscription = cartItems.some((item) => item.metadata?.type === "ehr-subscription");

      // Update your EHR checkout section in the checkout component:

      if (isEhrSubscription) {
        // EHR subscription workflow
        const createAccountResponse = await createEHRAccount(values, cartItems, cartTotal, stripeCouponObj, discount, originalSubtotal);
        if (!createAccountResponse) {
          setCheckoutErrorStatus(ehrAccountError || "Error creating EHR account.");
          return;
        }

        // For EHR subscriptions, check if we got setupIntentId directly from createEHRAccount
        if (createAccountResponse.setupIntentId) {
          // console.log("Direct EHR response with setupIntentId:", createAccountResponse.setupIntentId);

          navigateToOrderConfirmation({
            subscriptionPaymentIntentId: createAccountResponse.setupIntentId, // This will be setup intent for trials
          });
          return;
        }

        // Fallback to step function workflow if no direct setupIntentId
        setSetTimeoutIsLoading(true);
        setSetTimeoutError(null);

        setTimeout(async () => {
          try {
            const arnResults = await fetchExecutionArnResults(createAccountResponse.executionArn);
            setSetTimeoutIsLoading(false);

            // Check for both payment intent (regular) and setup intent (trial)
            const intentId = arnResults?.paymentIntentId || arnResults?.setupIntentId;

            if (intentId) {
              navigateToOrderConfirmation({
                subscriptionPaymentIntentId: intentId,
                subscriptionId: arnResults?.subscriptionId,
                isTrialSubscription: !!arnResults?.setupIntentId,
                addonPaymentIntentId: arnResults?.addonPaymentIntentId,
              });
              return;
            } else {
              console.error("No payment intent or setup intent found in results:", arnResults);
              setSetTimeoutError("Error processing EHR subscription. Please contact support.");
              return;
            }
          } catch (err) {
            console.error("Failed in automation step:", err);
            setSetTimeoutError("Error processing EHR subscription. Please contact support.");
            setSetTimeoutIsLoading(false);
          }
        }, 30000);
      } else {
        // Regular (non-EHR) workflow
        let createAccountResponse;
        if (hasPackageItem) {
          const purchaseMadeDetails = constructPurchaseMadeData(values, cartItems, cartTotal, originalSubtotal);
          createAccountResponse = await createStripeAccount(purchaseMadeDetails);
          if (!createAccountResponse) {
            setCheckoutErrorStatus(stripeAccountError || "Error creating account.");
            return;
          }
        }

        // Process Stripe payment for regular items
        const paymentResponse = await processPayment(
          values,
          cartTotal,
          stripeCouponObj,
          discount,
          originalSubtotal,
          cartItems,
          createAccountResponse // ! Why was this removed from Original Code? This is passed here as a param, then passed to constructPaymentData
        );
        const { paymentResults, subscription } = paymentResponse;
        if (!paymentResults && !subscription) {
          setCheckoutErrorStatus(stripeTransactionError || "Error processing Stripe payment.");
          return;
        }

        // Transaction automation for regular purchases
        const purchaseMadeAutomationDetails = constructPurchaseMadeData(values, cartItems, cartTotal, originalSubtotal, paymentResults, subscription);
        const automationResponse = await purchaseMade(purchaseMadeAutomationDetails);
        if (!automationResponse) {
          setCheckoutErrorStatus(purchaseAutomationError || "An error occurred while notifying the server about the Stripe purchase.");
        } else {
          // UPDATED: Handle both payment intent (regular) and setup intent (trial) for regular subscriptions too
          const subscriptionIntentId = subscription?.latest_invoice?.payment_intent?.id || subscription?.pending_setup_intent?.id;

          navigateToOrderConfirmation({
            subscriptionPaymentIntentId: subscriptionIntentId,
            subscriptionId: subscription?.id,
            isTrialSubscription: !!subscription?.pending_setup_intent,
            addonPaymentIntentId: paymentResults?.id,
          });
        }
      }
    } catch (error) {
      console.error("Checkout error:", error);
      setCheckoutErrorStatus(error.message);
    } finally {
      setCheckoutErrorStatus(null);
    }
  };

  const handleNavigate = () => {
    const productType = getCartProductType(cartItems);
    if (productType === "ehr") {
      navigate("/ehr-packages");
    } else {
      navigate("/ketamine-therapy/packages");
    }
  };

  const getLoadingMessage = () => {
    if (addressValidationLoading) return "validating your address";
    if (stripeAccountLoading) return "creating your account";
    if (ehrAccountLoading) return "creating your EHR account";
    if (setTimeoutIsLoading) return "finalizing your EHR subscription";
    if (stripeTransactionLoading) return "processing your payment";
    if (purchaseAutomationLoading) return "sending transaction details to crm";
    return null;
  };

  const getErrorMessage = () => {
    if (addressValidationError) return addressValidationError?.message || "Error validating address. Please try again.";
    if (stripeAccountError) return stripeAccountError;
    if (ehrAccountError) return ehrAccountError;
    if (setTimeoutError) return setTimeoutError;
    if (stripeTransactionError) return stripeTransactionError;
    if (purchaseAutomationError) return purchaseAutomationError?.message || "An error occurred while notifying the server about the purchase.";
    return null;
  };

  return (
    <StyledMainPageContainer id="checkout-page">
      <Helmet>
        <script
          async
          src={`https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&callback=initMap`}
        ></script>
      </Helmet>
      {(addressValidationLoading ||
        stripeAccountLoading ||
        ehrAccountLoading ||
        setTimeoutIsLoading ||
        stripeTransactionLoading ||
        purchaseAutomationLoading ||
        checkoutErrorStatus) && (
        <Container sx={{ marginBlock: "4rem" }}>
          <CBLoading loadingItem={getLoadingMessage()} type="checkout" />
          {/* {checkoutErrorStatus && <Alert severity="error">{checkoutErrorStatus}</Alert>} */}
        </Container>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={showBillingAddressFormFields ? validationSchema2 : validationSchema}
        onSubmit={(values, actions) => handleSubmit(values, actions)}
      >
        {({ isSubmitting, errors, touched, validateForm, setFieldValue, submitForm }) => {
          const handleAddressSelection = (selectedAddress) => {
            if (selectedAddress) {
              const addressFields = {
                shippingAddress: selectedAddress.shippingAddress,
                shippingAppartment: selectedAddress.shippingAppartment,
                shippingCity: selectedAddress.shippingCity,
                shippingState: selectedAddress.shippingState,
                shippingZipCode: selectedAddress.shippingZipCode,
                shippingZipCodeExtension: selectedAddress.shippingZipCodeExtension,
              };

              Object.keys(addressFields).forEach((key) => {
                setFieldValue(key, addressFields[key]);
              });
              setFieldValue("isAddressManuallyConfirmed", true);
              submitForm();
            }
          };
          return (
            <Form
              style={{
                display:
                  addressValidationLoading ||
                  stripeAccountLoading ||
                  ehrAccountLoading ||
                  setTimeoutIsLoading ||
                  stripeTransactionLoading ||
                  purchaseAutomationLoading
                    ? "none"
                    : "block",
              }}
            >
              <StyledCheckoutContainer>
                <MKTypography component="h1" variant="h1" sx={{ mb: 2 }}>
                  Checkout
                </MKTypography>
                <Grid container spacing={2}>
                  <CartSidebar cartTotal={cartTotal} cartItems={cartItems} />
                  <Grid item xs={12} lg={8} order={isLg ? 0 : 1}>
                    <Grid
                      container
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        border: `1px solid ${theme.palette.grey[400]}`,
                        padding: "1rem",
                        borderRadius: "12px",
                      }}
                    >
                      <Grid item>
                        {!hasPackageItem && (
                          <Alert sx={{ marginBottom: "1rem" }} severity="warning">
                            You must have a package in your cart to checkout.{" "}
                            <HashLink
                              style={{ color: theme.palette.secondary.main }}
                              scroll={(el) => {
                                const yOffset = -100;
                                const y = el.getBoundingClientRect().top + window.scrollY + yOffset;
                                window.scrollTo({ top: y, behavior: "smooth" });
                              }}
                              to={"/ketamine-therapy/packages#product-details-section"}
                            >
                              <br /> Click here to view packages.
                            </HashLink>
                          </Alert>
                        )}
                        {(addressErrors?.length > 0 || addressSuggestions?.length > 0) && (
                          <CustomAlert errors={addressErrors} suggestions={addressSuggestions} handleAddressSelection={handleAddressSelection} />
                        )}
                        {getErrorMessage() && (
                          <Alert sx={{ marginBottom: "1rem" }} severity="error">
                            {getErrorMessage()}
                          </Alert>
                        )}

                        <ContactInformation />
                      </Grid>
                      <Grid item>
                        <BillingAddress />
                      </Grid>
                      <Grid item>
                        <PaymentOptions />
                      </Grid>
                      <Grid item>
                        <MKBox
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            border: "1p solid red",
                            marginTop: "2rem",
                            maxWidth: "717px",
                          }}
                        >
                          <MKButton onClick={handleNavigate} variant="contained" color="secondary">
                            Continue Shopping
                          </MKButton>

                          <MKButton disabled={isSubmitting || !hasPackageItem} type="submit" variant="contained" color="secondary">
                            {isSubmitting ? "Processing..." : "Place Order"}
                          </MKButton>
                        </MKBox>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </StyledCheckoutContainer>
            </Form>
          );
        }}
      </Formik>
    </StyledMainPageContainer>
  );
};

export default Checkout;
